import {
  AvatarMeListingVariantSkuEnumItem,
  type PurchasedAvatarMeMediaItem,
} from '@ps/api/types';
import {
  type Listing,
  WearWithAiListingMediaType,
  type WithID,
} from '@ps/models';

import type { CarouselMedia } from '../../pdp/components/ProductImageCarousel';

/**
 * Build the merged carousel media list for the PDP, 1:1 with legacy
 * `ListingImageContainer.tsx`'s `medias` memo:
 *
 *  - **Creator (`isOwner`)** sees their own AI-generated media (from the
 *    API) PRE-PENDED to the listing's default + extra images. The
 *    listing-level `defaultWearWithAiMedia` / `extraWearWithAiMedias`
 *    are NOT included here — they're a curated subset for viewers.
 *  - **Viewer** sees only the creator's curated AI media — the
 *    `defaultWearWithAiMedia` + `extraWearWithAiMedias` fields on the
 *    listing — pre-pended to the listing's default + extra images.
 *    They never see the raw API media list.
 *
 * Trailing default images preserve the existing PDP hero behavior: if
 * a listing has no AI media at all, the result is just the regular
 * `defaultImage + extraImages`, matching the pre-Wear-With-AI carousel.
 */
export function buildPdpCarouselMedia({
  listing,
  isOwner,
  avatarMedia,
}: {
  listing: WithID<Listing>;
  isOwner: boolean;
  /** Result of `usePurchasedAvatarMeListingMedia` — used only for
   *  the creator branch. Pass `[]` (or omit) for the viewer. */
  avatarMedia?: PurchasedAvatarMeMediaItem[];
}): CarouselMedia[] {
  const defaults: CarouselMedia[] = [
    ...(listing.defaultImage
      ? [
          {
            url: listing.defaultImage,
            type: 'image' as const,
            tag: 'default' as const,
          },
        ]
      : []),
    ...(listing.extraImages ?? [])
      .filter(Boolean)
      .map(url => ({ url, type: 'image' as const, tag: 'default' as const })),
  ];

  if (isOwner) {
    // Match legacy: filter to items with a usable url and project
    // their variantSku → image/video type.
    const aiSlides: CarouselMedia[] = (avatarMedia ?? [])
      .filter(item => !!item.originalUrl || !!item.url)
      .map<CarouselMedia>(item => {
        const sku = item.request?.variantSku;
        const isImage =
          sku === AvatarMeListingVariantSkuEnumItem.ComPopstoreAvatarMeImage ||
          sku ===
            AvatarMeListingVariantSkuEnumItem.ComPopstoreAvatarMeStudioImage ||
          sku ===
            AvatarMeListingVariantSkuEnumItem.ComPopstoreAvatarMePhotoshoot ||
          sku ===
            AvatarMeListingVariantSkuEnumItem.ComPopstoreAvatarMeStudioPhotoshoot ||
          sku ===
            AvatarMeListingVariantSkuEnumItem.ComPopstoreAvatarMeStudioFreeImage;

        return {
          id: item.id,
          url: item.originalUrl ?? item.url!,
          type: isImage ? 'image' : 'video',
          tag: 'avatar-me',
          favorite: item.wearWithAi?.favorite,
          visible: item.wearWithAi?.visible,
        };
      });

    return uniqByUrl([...aiSlides, ...defaults]);
  }

  // Viewer branch — listing-level curated AI media.
  const curatedAi: CarouselMedia[] = [
    ...(listing.defaultWearWithAiMedia?.url
      ? [
          {
            url: listing.defaultWearWithAiMedia.url,
            type:
              listing.defaultWearWithAiMedia.type ===
              WearWithAiListingMediaType.Image
                ? ('image' as const)
                : ('video' as const),
            tag: 'avatar-me' as const,
          },
        ]
      : []),
    ...(listing.extraWearWithAiMedias ?? []).filter(Boolean).map(media => ({
      url: media.url,
      type:
        media.type === WearWithAiListingMediaType.Image
          ? ('image' as const)
          : ('video' as const),
      tag: 'avatar-me' as const,
    })),
  ];

  return uniqByUrl([...curatedAi, ...defaults]);
}

function uniqByUrl(items: CarouselMedia[]): CarouselMedia[] {
  const seen = new Set<string>();
  const out: CarouselMedia[] = [];
  for (const item of items) {
    if (seen.has(item.url)) continue;
    seen.add(item.url);
    out.push(item);
  }
  return out;
}
